import { DermloopClinicalActionTypeEnum } from '@dermloop/static-values';
import * as z from 'zod';

/**
 * Zod model for the Dermloop Form Language v1 meta-schema. Encodes the document
 * structure, tightened where the language rules are stricter than a plain
 * `allOf`/`if`/`then` JSON Schema shape.
 *
 * Structure only: open strings (`options.ref`, `imageType`, `assetKey`) and
 * cross-field rules (id uniqueness, bind slot names, condition resolution) are
 * publish-time registry checks in `validateFormTemplateDocument`, not here.
 */

export const FORM_LANGUAGE_VERSION = '1.0';

/**
 * Base locale every piece of translatable text must provide. Guarantees a
 * form always renders in at least one language regardless of author coverage.
 */
export const BASE_LOCALE = 'en';

/** Dotted path into the submission blob. */
const BIND_PATH_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*$/;
/**
 * Section / field id. Deliberately looser than `OPTION_VALUE_PATTERN` below: an
 * id is an internal anchor for `$ref`-style addressing and error paths, never
 * shown to a user, so it carries no casing rule and the published meta-schema
 * pins this exact character class.
 */
const ID_PATTERN = /^[a-zA-Z0-9-]+$/;
/**
 * Author-visible value literal — inline option values and the widget-owned fixed
 * vocabularies. Lowercase kebab-case ASCII tokens; capitalisation, punctuation
 * and locale live in `label`. Does not apply to `ref-data` selects, whose values
 * are row uuids resolved at render time and never appear in the document.
 */
const OPTION_VALUE_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const bindPathSchema = z.string().regex(BIND_PATH_PATTERN);

/** A condition reads a bind path or a widget output; only the field-id segment takes the id character class. */
const CONDITION_FIELD_PATTERN =
  /^(outputs\.[a-zA-Z0-9-]+\.[a-zA-Z_][a-zA-Z0-9_]*|[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)*)$/;
const conditionFieldSchema = z.string().regex(CONDITION_FIELD_PATTERN);

/**
 * User-facing text carried inline by the document itself — one string per
 * locale, keyed by locale code. Form content is self-contained (no i18n-key
 * indirection) so admin-authored forms need no code deploy to add strings.
 * The base locale must always be present.
 */
const translatableTextSchema = z
  .record(z.string(), z.string().min(1))
  .refine((value) => typeof value[BASE_LOCALE] === 'string', {
    message: `translatable text must include the "${BASE_LOCALE}" base locale`,
  });

/**
 * `bind` maps widget slot names to data paths. Slot names are structurally open
 * strings; their legality per widget is a publish-time registry check.
 */
const bindSchema = z
  .record(z.string(), bindPathSchema)
  .refine((value) => Object.keys(value).length >= 1, {
    message: 'bind must have at least one slot',
  });

/** A scalar condition operand value — no objects/arrays for eq/neq. */
const scalarSchema = z.union([z.string(), z.number(), z.boolean()]);

/**
 * Leaf condition. Modelled as a discriminated union on `op` so the
 * per-operator value rules are structural: eq/neq take a scalar, `in` takes a
 * non-empty array of scalars, `isSet` forbids a value.
 */
const leafConditionSchema = z.discriminatedUnion('op', [
  z.strictObject({
    field: conditionFieldSchema,
    op: z.literal('eq'),
    value: scalarSchema,
  }),
  z.strictObject({
    field: conditionFieldSchema,
    op: z.literal('neq'),
    value: scalarSchema,
  }),
  z.strictObject({
    field: conditionFieldSchema,
    op: z.literal('in'),
    value: z.array(scalarSchema).min(1),
  }),
  z.strictObject({
    field: conditionFieldSchema,
    op: z.literal('isSet'),
  }),
]);

/**
 * Combinator: exactly one of `allOf` / `anyOf`, each a non-empty array of
 * leaf conditions (one level only — no nested combinators).
 */
const combinatorSchema = z.union([
  z.strictObject({ allOf: z.array(leafConditionSchema).min(1) }),
  z.strictObject({ anyOf: z.array(leafConditionSchema).min(1) }),
]);

const conditionSchema = z.union([leafConditionSchema, combinatorSchema]);

const requiredRuleSchema = z.union([z.boolean(), conditionSchema]);

/** Inline option item. `exclusive` marks a choice that clears the others. */
const inlineOptionItemSchema = z.strictObject({
  value: z.string().regex(OPTION_VALUE_PATTERN),
  label: translatableTextSchema,
  exclusive: z.boolean().optional(),
});

/**
 * Options for `single-select` / `multi-select`: `inline` or `ref-data`.
 * `allowOther` is permitted only with `ref-data`.
 */
const selectOptionsSchema = z.discriminatedUnion('source', [
  z.strictObject({
    source: z.literal('inline'),
    items: z.array(inlineOptionItemSchema).min(1),
  }),
  z.strictObject({
    source: z.literal('ref-data'),
    ref: z.string().min(1),
    allowOther: z.boolean().optional(),
  }),
]);

/**
 * One authored clinical action. `disposition` is the plain-language sentence the
 * medical record states the outcome with ("Patient referred for specialist
 * assessment"); it is projected from the pinned template at read time and never
 * copied into a submission.
 */
const actionOptionLeafSchema = z.strictObject({
  value: z.string().regex(OPTION_VALUE_PATTERN),
  label: translatableTextSchema,
  disposition: translatableTextSchema.optional(),
});

const actionOptionGroupSchema = z.strictObject({
  value: z.string().regex(OPTION_VALUE_PATTERN),
  label: translatableTextSchema,
  options: z.array(actionOptionLeafSchema).min(1),
});

const actionOptionItemSchema = z.union([
  actionOptionLeafSchema,
  actionOptionGroupSchema,
]);

/**
 * Options for `clinical-action`. Deliberately not `selectOptionsSchema`: options
 * carry a disposition a `ref-data` source cannot, and `exclusive` is
 * meaningless for a single-select whose whole point is one decision.
 */
const actionOptionsSchema = z.strictObject({
  source: z.literal('inline'),
  items: z.array(actionOptionItemSchema).min(1),
});

/**
 * Options for `scale`: an ordered inline list, drawn in authored order. Inline
 * only, since the order is the meaning and a ref-data list has none an author
 * controls; at least two points, since one is not a scale.
 */
const scaleOptionsSchema = z.strictObject({
  source: z.literal('inline'),
  items: z
    .array(
      z.strictObject({
        value: z.string().regex(OPTION_VALUE_PATTERN),
        label: translatableTextSchema,
      }),
    )
    .min(2),
});

/**
 * location-picker params. `pickerType` fixes only the pin's shape (2d →
 * `{x,y}`, 3d → `{position,normal}`); which surface to render is the
 * host's concern. `assetKey` names that surface so a form can place more than
 * one picker of the same dimensionality (front/back body maps, several slices,
 * multiple avatars) and the host can tell them apart — an open string validated
 * at publish time against the asset-key registry.
 */
const locationPickerParamsSchema = z.strictObject({
  pickerType: z.enum(['2d', '3d']),
  assetKey: z.string().min(1).optional(),
});

/**
 * number params. Whole numbers only, so the bounds are integers too. `unit` is
 * shown beside the input and after the answer, and may use typesafe-i18n's
 * plural syntax (`minute{{s}}`), resolved against the answered number.
 */
const numberParamsSchema = z.strictObject({
  min: z.int().optional(),
  max: z.int().optional(),
  unit: translatableTextSchema.optional(),
});

/**
 * Common widget-field properties shared by every widget variant.
 * Widgets that declare no params omit `params` entirely, so the
 * strict object rejects any `params` key — chosen over accepting an empty
 * object for a clearer authoring error.
 */
const commonWidgetProps = {
  id: z.string().regex(ID_PATTERN),
  role: z.string().optional(),
  label: translatableTextSchema.optional(),
  summaryLabel: translatableTextSchema.optional(),
  bind: bindSchema.optional(),
  visibleWhen: conditionSchema.optional(),
  required: requiredRuleSchema.optional(),
};

/**
 * A display-only card: authored text a form states without asking anything. It
 * spells out its own props instead of spreading `commonWidgetProps`, so the
 * strict object rejects a `bind`, a `required` rule and a `summaryLabel` — a
 * field that holds no answer can declare none of the three, and authoring one
 * is a mistake worth failing on.
 *
 * A section-level field only, deliberately left out of `plainWidgetMembers`: a
 * group is one question with its follow-ups, and everything a group does with a
 * child — number it, count it towards the group's completeness, read it back on
 * the group's summary row, let it answer a `one-of` — a card can take no part
 * in. Page furniture sits between the cards, not inside one.
 */
const displayFieldSchema = z.strictObject({
  id: z.string().regex(ID_PATTERN),
  widget: z.literal('info'),
  label: translatableTextSchema.optional(),
  visibleWhen: conditionSchema.optional(),
  params: z.strictObject({ description: translatableTextSchema }),
});

/**
 * `addLabel` on top of the common props: a field carrying one starts behind a
 * button with that label instead of on screen, and a remove control clears it
 * again. Plain widgets only — `image-capture` and `location-picker` children are
 * drawn by their own group cards, which have no place to put the button.
 */
const plainWidgetProps = {
  ...commonWidgetProps,
  addLabel: translatableTextSchema.optional(),
};

/**
 * The widgets that compose freely inside a plain group. Kept as a member array
 * so both `plainWidgetFieldSchema` and the combined `widgetFieldSchema` are
 * built from one list.
 */
const plainWidgetMembers = [
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('yes-no-unknown'),
    params: z.strictObject({ allowUnknown: z.boolean().optional() }).optional(),
  }),
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('single-select'),
    options: selectOptionsSchema,
  }),
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('multi-select'),
    options: selectOptionsSchema,
  }),
  z.strictObject({ ...plainWidgetProps, widget: z.literal('text') }),
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('note'),
    placeholder: translatableTextSchema.optional(),
  }),
  z.strictObject({ ...plainWidgetProps, widget: z.literal('skin-type') }),
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('number'),
    params: numberParamsSchema.optional(),
  }),
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('diagnosis-tree'),
    params: z
      .strictObject({
        diagnoses: z.array(z.uuid()).min(1).optional(),
        shortlist: z.array(z.uuid()).min(1).optional(),
        mostUsed: z.literal(true).optional(),
      })
      .refine((params) => Object.keys(params).length > 0, 'Empty params')
      .optional(),
  }),
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('diagnosis-search'),
    params: z
      .strictObject({
        shortlist: z.array(z.uuid()).min(1).optional(),
        mostUsed: z.literal(true).optional(),
      })
      .refine((params) => Object.keys(params).length > 0, 'Empty params')
      .optional(),
  }),
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('clinical-action'),
    options: actionOptionsSchema,
  }),
  z.strictObject({
    ...plainWidgetProps,
    widget: z.literal('scale'),
    options: scaleOptionsSchema,
  }),
  z.strictObject({ ...plainWidgetProps, widget: z.literal('rating') }),
] as const;

/**
 * One image type per field. Several types are several `image-capture` fields
 * inside an `images` group — the capture profile (flash mode, detectors, guide
 * asset) is resolved 1:1 from the type, so a field holding two would silently
 * capture one of them under the wrong profile.
 *
 * `hero` names the photo a gallery leads with, which is otherwise whichever
 * slot the author happened to put first. At most one field per document may
 * claim it — a publish-time check, since the meta-schema sees one field at a
 * time.
 */
const imageCaptureFieldSchema = z.strictObject({
  ...commonWidgetProps,
  widget: z.literal('image-capture'),
  params: z.strictObject({
    imageType: z.string().min(1),
    hero: z.boolean().optional(),
  }),
});

const locationPickerFieldSchema = z.strictObject({
  ...commonWidgetProps,
  widget: z.literal('location-picker'),
  params: locationPickerParamsSchema,
});

const plainWidgetFieldSchema = z.discriminatedUnion(
  'widget',
  plainWidgetMembers,
);

const widgetFieldSchema = z.discriminatedUnion('widget', [
  ...plainWidgetMembers,
  imageCaptureFieldSchema,
  locationPickerFieldSchema,
]);

/**
 * How many of a group's children may hold a bound value. `each` — the default,
 * and what every group did before the property existed — lets every child hold
 * its own answer; `one-of` is an invariant over the bound values: at most one
 * child of the group holds one at a time. Kind-agnostic, because "only one of
 * these slots is filled" says the same thing about photos as about surfaces.
 *
 * A sibling property rather than a fourth `kind`: it is a second treatment over
 * the same children, not a different set of legal children.
 */
const groupSelectionSchema = z.enum(['each', 'one-of']);

/**
 * `summaryLabel` names the concept a read-only surface reads the group back
 * under — a journal line, a PDF export — where `title` is the capture question
 * ("Can the change be related to a habit or another cause?") and the summary
 * wants the noun ("Related cause"). A child carrying its own `summaryLabel`
 * asks to be read back as a line of its own instead of folding into the
 * group's.
 *
 * `required` gates submit on the group as a whole, in the same boolean-or-
 * condition shape a widget field uses. Under `one-of` it is the only gate the
 * group has: no single child can be the required one when any one of them
 * answers, so a child declaring `required` inside a `one-of` group is rejected
 * at publish time.
 */
const groupBodyCommon = {
  title: translatableTextSchema.optional(),
  summaryLabel: translatableTextSchema.optional(),
  collapsible: z.boolean().optional(),
  selection: groupSelectionSchema.optional(),
  required: requiredRuleSchema.optional(),
};

/**
 * A group declares what it is. `kind` selects which child widgets are legal, so
 * homogeneity is structural: `images` and `locations` promote to a dedicated
 * group-level card — a capture run with shared progress and one capture surface
 * across the group's slots — and the card speaks for the whole group, leaving a
 * sibling of another type nowhere to render. `plain` holds any of the other
 * widgets, which compose freely.
 *
 * `kind` names the region/treatment, not the child widget, so a second group
 * treatment over the same children stays expressible as a sibling property
 * (`{ kind: 'images', density: … }`), never as another kind.
 */
const groupFieldSchema = z.strictObject({
  id: z.string().regex(ID_PATTERN),
  role: z.string().optional(),
  visibleWhen: conditionSchema.optional(),
  group: z.discriminatedUnion('kind', [
    z.strictObject({
      kind: z.literal('images'),
      ...groupBodyCommon,
      fields: z.array(imageCaptureFieldSchema).min(1),
    }),
    z.strictObject({
      kind: z.literal('locations'),
      ...groupBodyCommon,
      fields: z.array(locationPickerFieldSchema).min(1),
    }),
    z.strictObject({
      kind: z.literal('plain'),
      ...groupBodyCommon,
      fields: z.array(plainWidgetFieldSchema).min(1),
    }),
  ]),
});

const fieldSchema = z.union([
  widgetFieldSchema,
  groupFieldSchema,
  displayFieldSchema,
]);

const sectionSchema = z.strictObject({
  id: z.string().regex(ID_PATTERN),
  title: translatableTextSchema.optional(),
  fields: z.array(fieldSchema).min(1),
});

export const actionParameterFormSchema = z.strictObject({
  title: translatableTextSchema.optional(),
  sections: z.array(sectionSchema).min(1),
});

export const executableActionLeafSchema = z.strictObject({
  value: z.string().regex(OPTION_VALUE_PATTERN),
  label: translatableTextSchema,
  disposition: translatableTextSchema.optional(),
  actionType: z
    .enum(DermloopClinicalActionTypeEnum)
    .exclude(['Referral', 'NoAction']),
  parameterForm: actionParameterFormSchema.optional(),
});

export type ExecutableActionOption = z.infer<typeof executableActionLeafSchema>;
export type ExecutableActionItem =
  | ExecutableActionOption
  | {
      value: string;
      label: z.infer<typeof translatableTextSchema>;
      options: ExecutableActionItem[];
    };

export const executableActionItemSchema: z.ZodType<ExecutableActionItem> =
  z.lazy(() =>
    z.union([
      executableActionLeafSchema,
      z.strictObject({
        value: z.string().regex(OPTION_VALUE_PATTERN),
        label: translatableTextSchema,
        options: z.array(executableActionItemSchema).min(1),
      }),
    ]),
  );

export const actionConfigurationSchema = z.strictObject({
  options: z.array(executableActionItemSchema).min(1),
});

export const configuredActionRequestSchema = z.strictObject({
  formTemplateVersionId: z.uuid(),
  optionValue: z.string().regex(OPTION_VALUE_PATTERN),
  parameters: z.strictObject({
    data: z.record(z.string(), z.unknown()),
    outputs: z.strictObject({}),
  }),
});

export const formTemplateDocumentSchema = z.strictObject({
  formLanguageVersion: z.literal(FORM_LANGUAGE_VERSION),
  title: translatableTextSchema.optional(),
  sections: z.array(sectionSchema).min(1),
  actionConfiguration: actionConfigurationSchema.optional(),
});

export {
  translatableTextSchema,
  bindPathSchema,
  conditionSchema,
  leafConditionSchema,
  combinatorSchema,
  selectOptionsSchema,
  actionOptionItemSchema,
  actionOptionLeafSchema,
  actionOptionGroupSchema,
  actionOptionsSchema,
  displayFieldSchema,
  scaleOptionsSchema,
  widgetFieldSchema,
  plainWidgetFieldSchema,
  imageCaptureFieldSchema,
  locationPickerFieldSchema,
  groupSelectionSchema,
  groupFieldSchema,
  fieldSchema,
  sectionSchema,
};
